import React, { useEffect, useState } from "react";

const AddJobs = () => {
    const [data, setData] = useState(null);
    const [newJob, setNewJob] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [editingJob, setEditingJob] = useState(null);
    const [editValue, setEditValue] = useState("");

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"; // fallback for dev

    useEffect(() => {
        fetch(`${apiBaseUrl}/datajobs`)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(setData)
            .catch((err) => {
                console.error("Failed to load jobs:", err);
            });
    }, [apiBaseUrl]);

    const handleAddJob = async () => {
        const name = newJob.trim();
        if (!name) return alert("Please enter a job name");

        const currentJobs = data?.states?.[0]?.jobs || [];
        if (currentJobs.some((j) => j.toLowerCase() === name.toLowerCase())) {
            return alert(`"${name}" already exists!`);
        }

        try {
            const res = await fetch(`${apiBaseUrl}/addjobs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ job: name }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Add failed");
            }

            const { data: freshData } = await res.json();
            setData(freshData);
            setNewJob("");
        } catch (err) {
            alert(err.message || "Could not add job");
        }
    };

    const handleDeleteJob = async (job) => {
        if (!window.confirm(`Delete "${job}"?`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/deletejobs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ job }),
            });

            if (!res.ok) throw new Error("Delete failed");

            const { data: freshData } = await res.json();
            setData(freshData);
        } catch (err) {
            alert("Could not delete job");
        }
    };

    const startEdit = (job) => {
        setEditingJob(job);
        setEditValue(job);
    };

    const handleCancelEdit = () => {
        setEditingJob(null);
        setEditValue("");
    };

    const handleSaveEdit = async () => {
        const newName = editValue.trim();
        if (!newName) return alert("Job name cannot be empty");

        if (newName.toLowerCase() === editingJob.toLowerCase()) {
            return alert("Name did not change");
        }

        try {
            const res = await fetch(`${apiBaseUrl}/updateJobs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    oldJob: editingJob,
                    newJob: newName,
                }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Update failed");
            }

            const { data: freshData } = await res.json();
            setData(freshData);
            setEditingJob(null);
            setEditValue("");
        } catch (err) {
            alert(err.message || "Could not update job");
        }
    };

    // ────────────────────────────────────────────────────────────────
    //   EARLY RETURN – MUST COME BEFORE ANY DATA ACCESS
    // ────────────────────────────────────────────────────────────────
    if (!data) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <p className="text-gray-600 text-lg">Loading jobs...</p>
            </div>
        );
    }

    // ──── Safe zone ──────────────────────────────────────────────────

    const jobs = Array.isArray(data?.states?.[0]?.jobs)
        ? data.states[0].jobs
        : [];

    const filteredJobs = jobs.filter((job) =>
        typeof job === "string" &&
        job.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Debug (remove after confirmation that list appears)
    console.log("Jobs loaded:", jobs.length, "filtered:", filteredJobs.length);

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-8">
            <div className="max-w-2xl mx-auto bg-white shadow-md rounded-lg p-6">
                <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">
                    Manage Jobs
                </h1>

                {/* Add */}
                <div className="flex gap-3 mb-6">
                    <input
                        type="text"
                        value={newJob}
                        onChange={(e) => setNewJob(e.target.value)}
                        placeholder="Add new job"
                        className="flex-1 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                        onClick={handleAddJob}
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
                    >
                        Add
                    </button>
                </div>

                {/* Search */}
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search jobs..."
                    className="w-full border border-gray-300 rounded px-4 py-2 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />

                {/* List */}
                {filteredJobs.length > 0 ? (
                    <ul className="space-y-3">
                        {filteredJobs.map((job, index) => (
                            <li
                                key={index}
                                className="flex items-center justify-between bg-gray-50 p-4 rounded shadow-sm"
                            >
                                {editingJob === job ? (
                                    <div className="flex gap-2 flex-1">
                                        <input
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            className="flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-green-400"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleSaveEdit();
                                                if (e.key === "Escape") handleCancelEdit();
                                            }}
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 text-sm"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="bg-gray-500 text-white px-4 py-1 rounded hover:bg-gray-600 text-sm"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="font-medium">{job}</span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => startEdit(job)}
                                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteJob(job)}
                                                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="text-center py-10 text-gray-500">
                        {searchTerm.trim()
                            ? "No matching jobs found"
                            : "No jobs yet — add one above"}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddJobs;